import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Pencil, Info, Search, Download, LineChart } from "lucide-react";
import { api, API } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { money, fmtDT } from "@/lib/semanas";
import { Var, usePublicaciones, PubSelect, pubLabel } from "@/components/control/cneShared";
import { RelacionesDialog } from "@/components/control/CneRelacionesDialog";
import { TendenciaDialog } from "@/components/control/CneTendencia";

function Ubicacion({ rels }) {
  if (!rels.length) return <span className="text-slate-400 italic">Sin asignar</span>;
  return (
    <div className="space-y-0.5">
      {rels.map((r) => (
        <div key={`${r.region}-${r.estado}-${r.municipio}`} className="text-slate-600 whitespace-nowrap">
          {r.municipio}, {r.estado} <span className="text-slate-400">· R{r.region} · {r.pct}%</span>
          {r.precio_kg == null && <span className="text-amber-600 text-[11px] ml-1">(sin precio en el periodo)</span>}
        </div>
      ))}
    </div>
  );
}

function Ponderado({ r, field, unit }) {
  if (!r.relaciones.length) return <span className="text-slate-300">—</span>;
  const v = r[field];
  if (v == null) return <span className="text-amber-600 text-xs" data-testid={`cne-pond-${unit}-na-${r.planta}`}>Sin información suficiente</span>;
  return <span className="tabular-nums font-semibold text-slate-900" data-testid={`cne-pond-${unit}-${r.planta}`}>{money(v)}</span>;
}

const matches = (r, q) => !q || [r.planta, ...r.relaciones.flatMap((x) => [x.municipio, x.estado, String(x.region)])]
  .some((s) => (s || "").toLowerCase().includes(q.toLowerCase()));

export function CnePlantas({ canEdit }) {
  const { structureKey, node } = useGlobal();
  const { data: pubs } = usePublicaciones();
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const [trend, setTrend] = useState(null);
  const [q, setQ] = useState("");
  const pubId = sel || pubs?.[0]?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["cne", "plantas", structureKey, node?.id ?? "entire", pubId],
    queryFn: async () => (await api.get("/control-semanal/cne/plantas", { params: { structure: structureKey, node_id: node?.id, pub_id: pubId } })).data,
    enabled: !!structureKey && !!pubId,
  });
  const items = (data?.items || []).filter((r) => matches(r, q));
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <PubSelect pubs={pubs} value={pubId} onChange={setSel} testid="cne-plantas-pub-select" />
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar planta, municipio o entidad" data-testid="cne-plantas-search"
            className="h-9 w-72 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 focus:outline-none" />
        </div>
        {data?.anterior && <span className="text-xs text-slate-500">Variación contra: {pubLabel(data.anterior)}</span>}
        {pubId && (
          <a href={`${API}/control-semanal/cne/plantas/export?${new URLSearchParams({ structure: structureKey, pub_id: pubId, ...(node?.id ? { node_id: node.id } : {}), ...(q ? { q } : {}) })}`}
            data-testid="cne-plantas-export-btn" className="ml-auto h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 flex items-center gap-1.5 hover:bg-slate-50">
            <Download className="w-4 h-4" />Exportar a Excel
          </a>
        )}
      </div>
      <div className="flex items-start gap-2 text-sm text-slate-600 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3" data-testid="cne-plantas-note">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        Precio ponderado = Σ (precio oficial × ponderación / 100) del periodo seleccionado. Las relaciones las valida un usuario; el sistema no las asigna automáticamente.
      </div>
      {isLoading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div> : (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full text-sm" data-testid="cne-plantas-table">
              <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-2.5">Planta</th><th className="text-left px-4 py-2.5">Relaciones territoriales (municipio · región · ponderación)</th><th className="text-right px-4 py-2.5">Precio ponderado $/kg</th><th className="text-right px-4 py-2.5">Precio ponderado $/litro</th><th className="text-right px-4 py-2.5">Var. $/kg vs semana anterior</th><th className="text-left px-4 py-2.5">Validado</th><th /></tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.planta} className="border-t border-slate-100 align-top" data-testid={`cne-planta-${r.planta}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800 whitespace-nowrap"><MapPin className="w-3.5 h-3.5 inline text-red-500 mr-1" />{r.planta}</td>
                    <td className="px-4 py-2.5"><Ubicacion rels={r.relaciones} /></td>
                    <td className="px-4 py-2.5 text-right"><Ponderado r={r} field="ponderado_kg" unit="kg" /></td>
                    <td className="px-4 py-2.5 text-right"><Ponderado r={r} field="ponderado_lt" unit="lt" /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums"><Var v={r.var_kg} /></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{r.updated_at ? `${r.updated_by_name} · ${fmtDT(r.updated_at)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">{r.relaciones.length > 0 && <button onClick={() => setTrend(r.planta)} title="Tendencia semanal" data-testid={`cne-planta-trend-${r.planta}`} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100"><LineChart className="w-4 h-4" /></button>}{canEdit && <button onClick={() => setEdit(r)} data-testid={`cne-planta-edit-${r.planta}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"><Pencil className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="cne-plantas-empty">Sin resultados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {edit && <RelacionesDialog row={edit} pubId={pubId} structure={structureKey} onClose={() => setEdit(null)} />}
      {trend && <TendenciaDialog planta={trend} onClose={() => setTrend(null)} />}
    </div>
  );
}
